import type { PostHog } from "posthog-js";
import type { AnalyticsEvent, AnalyticsEventMap } from "./events";

export type { NoProps } from "./events";

/**
 * PostHog, loaded lazily and never from the first-load bundle.
 *
 * ⚠️ NOTHING in this module may `import "posthog-js"` statically. The library
 * is ~60 KB gzipped before the replay recorder, and a static import anywhere
 * under `app/layout.tsx` puts all of it in the first-load JS of EVERY route —
 * including "/", whose byte budget the whole home-deck work was measured
 * against. The single `await import("posthog-js")` below is what keeps it in
 * its own async chunk, fetched after hydration (see `AnalyticsProvider`).
 *
 * It is also a NO-OP while `NEXT_PUBLIC_POSTHOG_KEY` is unset — same shape as
 * the API master key: local dev and preview deployments stay silent and never
 * pollute the production project unless someone opts in explicitly.
 */

// Read as full static expressions — Next only inlines `process.env.NEXT_PUBLIC_*`
// when it sees the whole member access, not a destructured or computed one.
const KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";
const AUTOCAPTURE_OFF = process.env.NEXT_PUBLIC_POSTHOG_AUTOCAPTURE === "off";
const REPLAY_OFF = process.env.NEXT_PUBLIC_POSTHOG_REPLAY === "off";

let client: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;
let failed = false;

/**
 * Calls that arrive before the chunk has loaded. Capped: a burst of clicks
 * during a slow network must cost a bounded amount of memory, and analytics is
 * never worth holding a page hostage for.
 */
const QUEUE_LIMIT = 50;
const queue: Array<(ph: PostHog) => void> = [];

export function isAnalyticsEnabled(): boolean {
  return Boolean(KEY) && typeof window !== "undefined";
}

function flush(ph: PostHog) {
  while (queue.length) {
    const fn = queue.shift();
    try {
      fn?.(ph);
    } catch (err) {
      console.warn("[analytics] queued call failed:", err);
    }
  }
}

/**
 * Loads + inits posthog-js once. Concurrent callers share one promise, and a
 * failed load is remembered so a broken CDN doesn't get re-fetched per click.
 */
export function loadAnalytics(): Promise<PostHog | null> {
  if (client) return Promise.resolve(client);
  if (loading) return loading;
  if (failed || !isAnalyticsEnabled()) return Promise.resolve(null);

  loading = import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(KEY as string, {
        api_host: HOST,
        // Pins the modern defaults: pageviews follow the History API
        // (`capture_pageview: "history_change"`), which is what an App Router
        // SPA navigation actually is. Without it we'd need a client component
        // reading useSearchParams on every route — and that forces a Suspense
        // boundary into the static home tree for no gain.
        defaults: "2025-05-24",
        // ⚠️ The one remaining lever, left OFF on purpose. "always" would
        // mint a person profile for every anonymous visitor too — strictly
        // more tracking, but person profiles are the billable unit in
        // PostHog and this is a mostly drive-by catalog, so flipping it is a
        // cost decision rather than a config one. Anonymous visitors already
        // produce full events AND session replays either way; they just
        // aren't stitched into a durable profile until they sign in.
        person_profiles: "identified_only",
        autocapture: !AUTOCAPTURE_OFF,
        capture_exceptions: true,
        // Click/scroll maps on top of the events autocapture already sends —
        // no extra billable events, just more detail on the same traffic.
        enable_heatmaps: true,
        disable_session_recording: REPLAY_OFF,
        session_recording: {
          // Defaults, restated because they're the privacy contract:
          // every <input> value is masked before it leaves the browser.
          maskAllInputs: true,
          // Opt-in text masking — put `data-ph-mask` on anything that renders
          // a person's own data (email in the account dropdown, etc).
          maskTextSelector: "[data-ph-mask]",
        },
        // Keeps emails/names out of autocaptured element text + URL params.
        mask_personal_data_properties: true,
        loaded: (ph) => {
          if (process.env.NODE_ENV === "development") ph.debug();
        },
      });
      client = posthog;
      flush(posthog);
      return posthog;
    })
    .catch((err) => {
      failed = true;
      queue.length = 0;
      console.warn("[analytics] failed to load posthog-js:", err);
      return null;
    });

  return loading;
}

/** Runs `fn` against the client, loading it first if that hasn't happened yet. */
function withClient(fn: (ph: PostHog) => void) {
  if (!isAnalyticsEnabled() || failed) return;
  if (client) {
    try {
      fn(client);
    } catch (err) {
      console.warn("[analytics] call failed:", err);
    }
    return;
  }
  if (queue.length < QUEUE_LIMIT) queue.push(fn);
  void loadAnalytics();
}

/**
 * Records one event from the catalog in `events.ts`.
 *
 * Safe to call from anywhere in the browser, at any time: before init, with
 * analytics disabled, or after a failed load. It never throws and never awaits.
 */
export function capture<E extends AnalyticsEvent>(
  event: E,
  ...rest: keyof AnalyticsEventMap[E] extends never
    ? []
    : [props: AnalyticsEventMap[E]]
): void {
  const props = rest[0];
  withClient((ph) => ph.capture(event, props));
}

type IdentityInput = {
  id: string;
  email?: string | null;
  plan?: string;
  subscriptionStatus?: string | null;
  /** Staff (the admin allowlist) — filtered out of product numbers. */
  isTeam?: boolean;
  /** A paid plan with no Creem subscription behind it (comped by hand). */
  isComped?: boolean;
};

/**
 * Last id we identified, so a re-render (or a token refresh firing
 * onAuthStateChange again) doesn't re-issue `identify` on every commit.
 */
let identifiedAs: string | null = null;

/**
 * Set once an explicit sign-out has reset the client. Until the sign-out
 * navigation replaces the document, `useUser` still holds the old user, and a
 * late plan re-render would otherwise identify the person who just left.
 */
let signingOut = false;

/** A sign-up waiting for `identifyUser`, so it lands on the identified person. */
let pendingSignUp: { userId: string; method: string } | null = null;

export function identifyUser({
  id,
  email,
  plan,
  subscriptionStatus,
  isTeam = false,
  isComped = false,
}: IdentityInput): void {
  if (signingOut) return;
  const props = {
    plan,
    subscription_status: subscriptionStatus ?? null,
    is_team: isTeam,
    is_comped: isComped,
  };
  if (identifiedAs === id) {
    // Same person — only the plan can have moved (upgrade, lapse). Person
    // properties are cheap to re-set; `identify` is not.
    withClient((ph) => {
      ph.setPersonProperties(props);
      registerTeam(ph, isTeam);
    });
    return;
  }
  identifiedAs = id;
  withClient((ph) => {
    ph.identify(id, { email: email ?? undefined, ...props });
    registerTeam(ph, isTeam);
    flushSignUp(ph, id);
  });
}

/**
 * `is_team` as a SUPER-property too, not only on the person: it then rides on
 * every event of the session (pageviews, autocapture), so staff traffic can be
 * filtered without a person join. `reset()` clears it on sign-out.
 */
function registerTeam(ph: PostHog, isTeam: boolean) {
  if (isTeam) ph.register({ is_team: true });
  else ph.unregister("is_team");
}

/**
 * Reports a sign-in AT MOST ONCE per tab per user.
 *
 * ⚠️ `onAuthStateChange` does NOT only fire `SIGNED_IN` on a real sign-in:
 * with an existing session it fires again on every full page load, as the
 * session is restored. Measured on prod 2026-09-22 — one genuine email login
 * produced two `signed in` events 23 s apart, the second one after a reload.
 * Filtering out `INITIAL_SESSION` (which is what the code used to do, and what
 * the comment claimed was enough) does not catch this.
 *
 * `sessionStorage` is the right scope: it survives reloads and SPA navigation
 * within the tab, and it is gone in a new tab — where a genuine sign-in should
 * be reported again. It can throw (private mode, blocked storage), so a
 * failure degrades to reporting the event rather than losing it.
 *
 * The mark is written only AFTER the event was handed to posthog-js. Email
 * sign-in reloads the page right after `SIGNED_IN`; a mark set before the
 * library had loaded outlived the queued event, which died with the page, and
 * then suppressed the retry the reload would have made.
 */
const SIGNIN_KEY = "analytics:signed-in-reported";
let signInQueuedFor: string | null = null;

export function captureSignIn(userId: string, method: string): void {
  if (signInQueuedFor === userId) return;
  try {
    if (sessionStorage.getItem(SIGNIN_KEY) === userId) return;
  } catch {
    // Storage unavailable — fall through and report.
  }
  signInQueuedFor = userId;
  withClient((ph) => {
    ph.capture("signed in", { method });
    try {
      sessionStorage.setItem(SIGNIN_KEY, userId);
    } catch {
      // The in-memory guard still covers this document.
    }
  });
}

export function clearSignInMark(): void {
  signInQueuedFor = null;
  try {
    sessionStorage.removeItem(SIGNIN_KEY);
  } catch {
    // Nothing to do; the mark is a de-duplication hint, not state we rely on.
  }
}

/**
 * Reports a new account ONCE, next to its "signed in". The caller decides it
 * is a first session (`isFirstSession`); this only de-duplicates — in
 * `localStorage`, because that first session survives reloads and new tabs
 * (`last_sign_in_at` doesn't move on a token refresh) and each would report
 * it again.
 *
 * Held until `identifyUser` has run for that id, so the event follows the
 * identify call and lands on the person instead of the anonymous id.
 */
const SIGNUP_KEY_PREFIX = "analytics:signed-up:";

export function captureSignUp(userId: string, method: string): void {
  try {
    if (localStorage.getItem(SIGNUP_KEY_PREFIX + userId)) return;
  } catch {
    // Storage unavailable — report; a duplicate beats a lost sign-up.
  }
  if (pendingSignUp?.userId === userId) return;
  pendingSignUp = { userId, method };
  if (identifiedAs === userId) withClient((ph) => flushSignUp(ph, userId));
}

function flushSignUp(ph: PostHog, userId: string) {
  const pending = pendingSignUp;
  if (!pending || pending.userId !== userId) return;
  pendingSignUp = null;
  ph.capture(
    "signed up",
    { method: pending.method },
    {
      $set_once: {
        signed_up_at: new Date().toISOString(),
        signup_method: pending.method,
      },
    },
  );
  try {
    localStorage.setItem(SIGNUP_KEY_PREFIX + userId, "1");
  } catch {
    // Best effort.
  }
}

/**
 * An explicit sign-out (ConfirmLogoutModal), BEFORE the form posts to
 * /api/auth/sign-out. That route signs out on the server and answers with a
 * full navigation, so the browser client never sees SIGNED_OUT — without this
 * the event was never captured, and `reset()` never ran either: posthog-js kept
 * the person's id in its storage, and every later anonymous visit on that
 * browser was attributed to them.
 *
 * Waits at most ~800 ms for the client. When it isn't there in time, a marker
 * in sessionStorage lets the next document (`resetUser`) finish the job.
 */
const SIGN_OUT_PENDING_KEY = "analytics:sign-out-pending";
const SIGN_OUT_WAIT_MS = 800;

export async function prepareSignOut(): Promise<void> {
  if (!isAnalyticsEnabled()) return;
  signingOut = true;
  try {
    sessionStorage.setItem(SIGN_OUT_PENDING_KEY, "1");
  } catch {
    // No marker: the in-page path below is then the only one.
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const ph = await Promise.race([
    loadAnalytics(),
    new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), SIGN_OUT_WAIT_MS);
    }),
  ]);
  clearTimeout(timer);
  if (!ph) return;
  try {
    // sendBeacon survives the navigation the sign-out form starts next.
    ph.capture(
      "signed out",
      { reason: "explicit" },
      { transport: "sendBeacon", send_instantly: true },
    );
    ph.reset();
  } catch (err) {
    console.warn("[analytics] sign-out failed:", err);
  }
  identifiedAs = null;
  pendingSignUp = null;
  clearSignInMark();
  clearSignOutMarker();
}

function hasSignOutMarker(): boolean {
  try {
    return sessionStorage.getItem(SIGN_OUT_PENDING_KEY) !== null;
  } catch {
    return false;
  }
}

function clearSignOutMarker() {
  try {
    sessionStorage.removeItem(SIGN_OUT_PENDING_KEY);
  } catch {
    // Best effort.
  }
}

/**
 * The viewer is signed out. `reset()` mints a fresh anonymous id so the next
 * visitor on a shared browser is not stitched onto the previous person's
 * profile.
 *
 * Two ways here:
 *  - after an explicit sign-out that `prepareSignOut` could not finish (the
 *    client wasn't loaded): the marker says so, and posthog-js still holds the
 *    person who left in its storage — report for them, then reset;
 *  - a session that ended in this page (another tab signed out, the refresh
 *    token died): an identified -> null transition we saw happen.
 *
 * Only the marker can trigger a reset on a fresh document — never "stored id
 * + no user" alone: a failed `getClaims()` reads as signed out for a moment.
 */
export function resetUser(): void {
  if (hasSignOutMarker()) {
    identifiedAs = null;
    pendingSignUp = null;
    clearSignInMark();
    withClient((ph) => {
      if (ph.get_property("$user_state") === "identified") {
        ph.capture("signed out", { reason: "explicit" });
        ph.reset();
      }
      clearSignOutMarker();
    });
    return;
  }
  if (identifiedAs === null) return;
  // Captured BEFORE reset, so it is attributed to the person who left rather
  // than to the fresh anonymous id. The early return above means this only
  // fires on a real identified -> null transition, never on an anon page load.
  capture("signed out", { reason: "session_ended" });
  identifiedAs = null;
  pendingSignUp = null;
  clearSignInMark();
  withClient((ph) => ph.reset());
}

/** Test hook: forget module state between cases. */
export function resetAnalyticsStateForTests(): void {
  identifiedAs = null;
  signingOut = false;
  pendingSignUp = null;
  signInQueuedFor = null;
}
